import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "page-content" }
const _hoisted_2 = { class: "buttons-container" }
const _hoisted_3 = ["disabled"]
const _hoisted_4 = { class: "table-wrapper" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = ["title"]
const _hoisted_7 = {
  key: 1,
  class: "custom-header",
  style: {}
}
const _hoisted_8 = { class: "sort-arrow" }
const _hoisted_9 = { key: 2 }

import { ref, onMounted, computed } from 'vue';
	import { useRouter, useRoute } from 'vue-router';
	import axios from 'axios';
	import Loader from '@/components/Loader.vue';
	import Page from "@/components/v2/layout/Page.vue";
	import functions from '@/use/functions';
	import { Permissions, PermissionLevels } from '@/models/permissions';
	import PermissionsWrapper from '@/components/wrappers/PermissionsWrapper.vue';
	import BatchHeader from './BatchHeader.vue';
	import BatchDisbursementRow from './BatchDisbursementRow.vue';
	import { DisbursementBatchDetailsModel, DisbursementBatchReferenceModel } from '@/models/disbursementBatchesV2';
	import Breadcrumbs from "@/components/shared/Breadcrumbs.vue";

	interface Header {
		label: string;
		sortable: boolean;
		key: string;
		sortArrow: string;
		title?: string;
		customLabel?: {
			line1?: string;
			line2?: string;
		};
		maxWidth?: string;
	}

	
export default /*@__PURE__*/_defineComponent({
  __name: 'BatchDetails',
  setup(__props) {

	const route = useRoute();
	const router = useRouter();

	const batch = ref<DisbursementBatchDetailsModel | null>(null);

	const loading = ref(false);
	const isProcessing = ref(false);

	const headers = computed<Array<Header>>(() => [
		{ label: 'In|Out', sortable: true, key: 'isDeleted', sortArrow: getSortArrow('isDeleted') },
		{ label: 'Credit Union', sortable: true, key: 'lenderName', sortArrow: getSortArrow('lenderName')},
		{ label: 'Borrower (F/L)', sortable: true, key: 'borrowerName',   sortArrow: getSortArrow('borrowerName')},
		{ label: 'Sch Disb Amt', sortable: true, key: 'disbursementAmount', title: 'Scheduled Disbursement Amount', sortArrow: getSortArrow('disbursementAmount')},
		{ label: 'Disb Method', sortable: true, key: 'disbursementMethodHumanized', title: 'Disbursement Method', sortArrow: getSortArrow('disbursementMethodHumanized')},
		{ label: 'Disb Date', sortable: true, key: 'disbursementDateFormatted', sortArrow: getSortArrow('disbursementDateFormatted')},
		{ label: 'Reference', sortable: true, key: 'referenceId', sortArrow: getSortArrow('referenceId')},
		{ label: 'Lender Use (LoanID + DisbID)', customLabel: { line1: 'Lender Use', line2: '(LoanID + DisbID)'}, sortable: true, key: 'lenderUseOnly', maxWidth: '110px', sortArrow: getSortArrow('lenderUseOnly')},
		{ label: 'Disb Seq #', sortable: true, key: 'sequenceNumber', title: 'Disbursement Sequence Number', sortArrow: getSortArrow('sequenceNumber')},
	]);

	const getReferenceDisbursement = (disbursementId: number) => batch.value?.disbursements.find(d => d.id == disbursementId);

	const fetchBatchDetailsAsync = async () => {
		try {
			const response = await axios.get(`/api/v2/disbursement-batches/${route.params.batchId}`);

			batch.value = response.data;
		} catch (error) {
			console.error(error);

			functions.openModal({
				title: 'Oops!',
				description: `Failed to get details for disbursement batch ${route.params.batchId}. Please try again later.`
			});
		} finally {
			loading.value = false;
		}
	};

	const approveBatchAsync = async () => {
		if (isProcessing.value) {
			return;
		}

		isProcessing.value = true;
		loading.value = true;

		try {
			await axios.post(`/api/v2/disbursement-batches/${route.params.batchId}/approve`);
			await fetchBatchDetailsAsync();
		} catch (error) {
			console.error(error);

			functions.openModal({
				title: 'Oops!',
				description: `Failed to approve disbursement batch ${route.params.batchId}. Please try again later.`
			});
		} finally {
			isProcessing.value = false;
			loading.value = false;
		}
	};

	const goBack = () => router.go(-1);

	const sortKey = ref('');
	const sortOrder = ref(1);

	const sortedDisbursementBatchReferences = computed<DisbursementBatchReferenceModel[]>(() => {
		if (!batch.value) {
			return [];
		} else if (!sortKey.value) {
			return batch.value.disbursementBatchReferences; // no sorting, return original order
		} else if (sortKey.value === 'isDeleted') {
			return sortByInOut([...batch.value.disbursementBatchReferences], sortKey.value)
		}

		return sortByProperty([...batch.value.disbursementBatchReferences], sortKey.value);
	});

	const sortBy = (key: string) => {
		if (sortKey.value === key) {
			if (sortOrder.value === 1) {
				sortOrder.value = -1; // second click, descending
			} else if (sortOrder.value === -1) {
				sortKey.value = ''; // third click, reset sort
				sortOrder.value = 1; // default sort order for the next click
			}
		} else {
			sortKey.value = key;
			sortOrder.value = 1; // first click, ascending
		}
	};

	const getSortArrow = (key: string) => {
		if (sortKey.value === key) {
			return sortOrder.value === 1 ? '▲' : sortOrder.value === -1 ? '▼' : ' ';
		}
		return '';
	};

	const isActiveSort = (key: string) => (sortKey.value === key ? 'active-sort' : '');

	const sortByInOut = (arr, property: string) => {
		return arr.sort((a, b)=> {
			return sortOrder.value === 1 ? Number(b[property]) - Number(a[property]) :Number(a[property]) - Number(b[property]) ;
		});
	} 
	
	const handleHeaderClick = (header: Header) => {
		if (header.sortable) {
			sortBy(header.key)
		}
	}

	function sortByProperty(
		arr: DisbursementBatchReferenceModel[],
		property: string,
	): DisbursementBatchReferenceModel[] {
		return [...arr].sort((a, b) => {		
			const disA = batch.value!.disbursements.find(d => d.id === a.disbursementId);
    	const disB = batch.value!.disbursements.find(d => d.id === b.disbursementId);

			let valA = disA ? disA[property] : undefined;
			let valB = disB ? disB[property] : undefined;
			
			// Sorts by property with undefined first when ASC
			if (sortOrder.value === 1) {
				if (valA === undefined && valB !== undefined) return -1;
				if (valB === undefined && valA !== undefined) return 1;
			}

			// Sorts by property with undefined last when DESC
			if (sortOrder.value === -1) {
				if (valA === undefined && valB !== undefined) return 1;
				if (valB === undefined && valA !== undefined) return -1;
			}

			
			// Fallback for when both are defined or both are undefined.
			// For string properties, use localeCompare(). For numbers, use subtraction.
			// Uses localeCompare for natural sorting, considering numbers within strings
			if (typeof valA === 'string' && typeof valB === 'string') {
				valA = valA.toLowerCase();
				valB = valB.toLowerCase();

				switch (sortOrder.value) {
					case 1: return valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' });
					case -1: return valB.localeCompare(valA, undefined, { numeric: true, sensitivity: 'base' });
					default: return 0;
				}
			}

			if (typeof valA === 'number' && typeof valB === 'number') {
				switch (sortOrder.value) {
					case 1: return  valA - valB ;
					case -1: return valB - valA;
					default: return 0;
				}
			}

			// For other types, or if both are undefined, preserve original order.
			return 0;
		});
	}

	onMounted(async () => {
		loading.value = true;

		try {
			await fetchBatchDetailsAsync();
		} catch (error) {
			console.error(error);
		} finally {
			loading.value = false;
		}
	});

return (_ctx: any,_cache: any) => {
  return (loading.value)
    ? (_openBlock(), _createBlock(Loader, { key: 0 }))
    : (_openBlock(), _createBlock(Page, { key: 1 }, {
        default: _withCtx(() => [
          _createVNode(Breadcrumbs, {
            "root-path": "/",
            showJumpTo: true
          }),
          _createElementVNode("div", _hoisted_1, [
            _createVNode(BatchHeader, { batch: batch.value }, null, 8, ["batch"]),
            _createElementVNode("div", _hoisted_2, [
              _createVNode(PermissionsWrapper, {
                permissionIds: [_unref(Permissions).DisbursementBatches],
                permissionLevel: _unref(PermissionLevels).Update
              }, {
                default: _withCtx(() => [
                  _createElementVNode("button", {
                    disabled: batch.value?.isApproved || isProcessing.value,
                    onClick: approveBatchAsync
                  }, " Approve ", 8, _hoisted_3)
                ]),
                _: 1
              }, 8, ["permissionIds", "permissionLevel"]),
              _createElementVNode("button", {
                class: "secondary",
                onClick: goBack
              }, "Back")
            ]),
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("table", null, [
                _createElementVNode("thead", null, [
                  _createElementVNode("tr", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(headers.value, (header) => {
                      return (_openBlock(), _createElementBlock("th", {
                        key: header.key,
                        style: _normalizeStyle({ maxWidth: header?.maxWidth || 'none' }),
                        onClick: ($event: any) => (handleHeaderClick(header))
                      }, [
                        (header.sortable && !header.customLabel)
                          ? (_openBlock(), _createElementBlock("span", {
                              key: 0,
                              class: _normalizeClass(isActiveSort(header.key)),
                              title: header.title
                            }, _toDisplayString(header.label) + " " + _toDisplayString(getSortArrow(header.key)), 11, _hoisted_6))
                          : (header.sortable && header.customLabel)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                _createElementVNode("span", {
                                  class: _normalizeClass(["label", isActiveSort(header.key)])
                                }, [
                                  _createTextVNode(_toDisplayString(header.customLabel.line1) + " ", 1),
                                  _cache[0] || (_cache[0] = _createElementVNode("br", null, null, -1)),
                                  _createTextVNode(" " + _toDisplayString(header.customLabel?.line2), 1)
                                ], 2),
                                _createElementVNode("span", _hoisted_8, _toDisplayString(header.sortArrow), 1)
                              ]))
                            : (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(header.label), 1))
                      ], 12, _hoisted_5))
                    }), 128))
                  ])
                ]),
                _createElementVNode("tbody", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedDisbursementBatchReferences.value, (reference) => {
                    return (_openBlock(), _createBlock(BatchDisbursementRow, {
                      key: reference.disbursementId,
                      batchId: batch.value?.id,
                      reference: reference,
                      disbursement: getReferenceDisbursement(reference.disbursementId),
                      isBatchApproved: batch.value?.isApproved,
                      isProcessing: isProcessing.value,
                      onBatchUpdateAsync: fetchBatchDetailsAsync
                    }, null, 8, ["batchId", "reference", "disbursement", "isBatchApproved", "isProcessing"]))
                  }), 128))
                ])
              ])
            ])
          ])
        ]),
        _: 1
      }))
}
}

})